import React, { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query-v4';
import OCPLayout from '../layout/OCPLayout';
import FlowCanvas from '../components/FlowCanvas';
import DetailPanel from '../components/detailPanel/DetailPanel';
import { queryClient, findOCPWorkflows } from '../api';
import { useOCPStore } from '../hooks/useOCPStore';
import { useFlowVersionControl } from '../hooks/useFlowVersionControl';
import TestFunnelModal from '../components/TestFunnelModal';
import { useBypassWhitelistStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useBypassWhitelistStore';
import { useParams } from 'react-router-dom';
import { IOnboardingFlow } from '@scale/shared/onboardingControlPanel';
import { Spinner } from '@scale/scaleui';
import { IUserJson } from '@scale/shared/types/user';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const OnboardingFlowPage: React.FC<{ user: IUserJson }> = ({ user }) => {
  const { flowId } = useParams<{ flowId: string }>();

  const {
    currentFlow,
    setCurrentFlow,
    flowVersions,
    addFlowVersions,
    setFinishedFetchingSignupFlowDraft,
    setIsValidSignupFlowName,
    setChangedSignupFlowOnboardingFlow,
    setCanFreeEdit,
  } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    setCurrentFlow: state.setCurrentFlow,
    flowVersions: state.flowVersions,
    addFlowVersions: state.addFlowVersions,
    setFinishedFetchingSignupFlowDraft: state.setFinishedFetchingSignupFlowDraft,
    setIsValidSignupFlowName: state.setIsValidSignupFlowName,
    setChangedSignupFlowOnboardingFlow: state.setChangedSignupFlowOnboardingFlow,
    setCanFreeEdit: state.setCanFreeEdit,
  }));
  const { loadBypassWhitelist } = useBypassWhitelistStore(state => ({
    loadBypassWhitelist: state.loadBypassWhitelist,
  }));
  const [isLoading, setIsLoading] = useState(true);
  const canFreeEdit = useUnleashFeatureFlag('ocp-free-edit', { userId: user!._id }, false);

  const loadAndSetCurrentFlow = async (flowWithVersions: {
    [version: string]: IOnboardingFlow;
  }) => {
    if (!flowWithVersions) {
      return;
    }
    setFinishedFetchingSignupFlowDraft(false);
    // OCP flow starts off with valid signup flow name and no changes to signup flow onboarding flow
    setIsValidSignupFlowName(true);
    setChangedSignupFlowOnboardingFlow(false);
    const mostRecentVersion = Object.keys(flowWithVersions).sort(
      (a, b) => Number(b) - Number(a),
    )[0];
    setCurrentFlow(flowWithVersions[mostRecentVersion]);
    await loadBypassWhitelist(flowWithVersions[mostRecentVersion]);
  };

  const fetchFlowsAndSetCurrentFlow = async (flowId: string) => {
    const flows = await findOCPWorkflows();
    const newFlowVersions = addFlowVersions(flows);
    await loadAndSetCurrentFlow(newFlowVersions[flowId]);
  };

  useEffect(() => {
    (async () => {
      if (flowId && flowId !== 'draft' && (!currentFlow || currentFlow._id !== flowId)) {
        const flow = flowVersions[flowId as string];
        if (flow) {
          await loadAndSetCurrentFlow(flow);
        } else {
          await fetchFlowsAndSetCurrentFlow(flowId);
        }
      }
      setIsLoading(false);
    })();
  }, [setCurrentFlow, currentFlow, flowId]);

  useFlowVersionControl();

  useEffect(() => {
    document.title = 'GenAI - Onboarding Control Panel';
  }, []);

  useEffect(() => {
    setCanFreeEdit(!!canFreeEdit);
  }, [canFreeEdit]);

  if (!isLoading) {
    return (
      <QueryClientProvider client={queryClient}>
        <OCPLayout>
          <FlowCanvas />
          <DetailPanel />
          <TestFunnelModal />
        </OCPLayout>
      </QueryClientProvider>
    );
  } else {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner size="lg" className="m-2" />
      </div>
    );
  }
};

export default OnboardingFlowPage;
